import { useState, useCallback, useEffect, useRef } from 'react'
import { Button, Icon, Flex, Text, VStack, useToast, Box } from '@chakra-ui/react'
import { DragGridIcon, WarningIcon } from '../../../assets/icons'
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd'
import { useTranslation } from 'react-i18next'
import MasterCard from '../../../components/contentcards/base/MasterCard'
import SideBar from '../../../components/contentcards/base/SideBar'
import { ContentType } from '../../../types/content/content'
import { fetchContent } from '../../../api/content/content'
import handleToast from '../../../components/HandleToast'
import { ContentItemType } from '../../../types/content/contentItems'
import { contentItems } from '../../../hooks/content/contentItems'
import { useQueryClient } from '@tanstack/react-query'
import { patchContent, completeContent } from '../../../api/content/content'
import { addContentResponse } from '../../../api/content/contentResponse'
import { content } from '../../../hooks/content/content'
import { useRole } from '../../../hooks/auth/useRole'
import { CONTENT_EXPERIENCE } from './contentExperienceStyles'

interface AboutContentQuestionsProps {
  contentId: string
  contentType: ContentType
  isDraft: boolean
  stopSidebar: boolean
  isModal?: boolean
  onComplete?: () => void
  onEditNavigate?: () => void
}

const AboutContentQuestions: React.FC<AboutContentQuestionsProps> = ({
  contentId,
  contentType,
  isDraft,
  stopSidebar = true,
  isModal = false,
  onComplete,
  onEditNavigate,
}) => {
  const { t } = useTranslation()
  const { isAdmin, isUserMode } = useRole()
  const {
    useGetContentItemsListQuery,
    createContentItemMutation,
    deleteContentItemMutation,
    patchContentItemMutation,
  } = contentItems()
  const queryClient = useQueryClient()

  const { patchPublishContentMutation } = content()

  const contentItemsQuery = contentId
    ? useGetContentItemsListQuery({ id: contentId })
    : { data: null, isLoading: false }

  const [oldQuestions, setOldQuestions] = useState<any[]>([])
  const [newQuestions, setNewQuestions] = useState<any[]>([])
  const [questionErrors, setQuestionErrors] = useState<Record<number, string>>({})
  const [isEditing, setIsEditing] = useState(false)
  const [isCompleted, setIsCompleted] = useState(false)
  const [contentData, setContentData] = useState<any>(null)
  const learningUrl = typeof contentData?.url === 'string' ? contentData.url.trim() : ''
  const normalizedLearningUrl =
    learningUrl && /^https?:\/\//i.test(learningUrl) ? learningUrl : learningUrl ? `https://${learningUrl}` : ''
  const canEditContent = (isModal || (isAdmin && !isUserMode)) && !!onEditNavigate
  const showTakingMode = !isEditing && (!isAdmin || isUserMode || isModal)

  // Fetch content data for Learning URL
  useEffect(() => {
    if (contentType === ContentType.Learning && contentId) {
      fetchContent(contentId.toString()).then(setContentData)
    }
  }, [contentType, contentId])

  // Capture survey/learning responses in view (quiz) mode
  const [surveyMCQSelections, setSurveyMCQSelections] = useState<Record<string, number | null>>({})
  const [surveyRatingSelections, setSurveyRatingSelections] = useState<Record<string, number | null>>({})
  const [surveyTextAnswer, setSurveyTextAnswer] = useState<Record<string, string | null>>({})

  const [sidebarPosition, setSidebarPosition] = useState<'start' | 'fixed' | 'end'>('start')
  const startComponentRef = useRef<HTMLDivElement>(null)
  const endComponentRef = useRef<HTMLDivElement>(null)
  const sidebarRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (contentItemsQuery.data) {
      let loadedQuestions: any[] = []
      if (Array.isArray(contentItemsQuery.data)) {
        loadedQuestions = contentItemsQuery.data
      } else if (contentItemsQuery.data.data && Array.isArray(contentItemsQuery.data.data)) {
        loadedQuestions = contentItemsQuery.data.data
      }
      setOldQuestions(loadedQuestions.map((q) => ({ ...q })))
      setNewQuestions(loadedQuestions.map((q) => ({ ...q })))
    }
  }, [contentItemsQuery.data])

  useEffect(() => {
    const startObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSidebarPosition('start')
        } else if (!entry.isIntersecting) {
          setSidebarPosition('fixed')
        }
      },
      { threshold: [0, 1] },
    )

    const endObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSidebarPosition('end')
        }
      },
      { threshold: [0] },
    )

    if (startComponentRef.current) {
      startObserver.observe(startComponentRef.current)
    }

    if (endComponentRef.current) {
      endObserver.observe(endComponentRef.current)
    }

    return () => {
      if (startComponentRef.current) {
        startObserver.unobserve(startComponentRef.current)
      }
      if (endComponentRef.current) {
        endObserver.unobserve(endComponentRef.current)
      }
    }
  }, [])

  const updateIndices = (questions: any[]) => {
    return questions.map((q, idx) => {
      const updatedQuestion = { ...q, index: idx }
      return updatedQuestion
    })
  }

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result
    if (!destination) return
    setNewQuestions((prev) => {
      const newQs = [...prev]
      const [removed] = newQs.splice(source.index, 1)
      newQs.splice(destination.index, 0, removed)
      removed.index = destination.index
      return newQs
    })
  }

  const moveQuestion = useCallback((dragIndex: number, hoverIndex: number) => {
    setNewQuestions((prev) => {
      const newQs = [...prev]
      const [removed] = newQs.splice(dragIndex, 1)
      newQs.splice(hoverIndex, 0, removed)
      removed.index = hoverIndex
      return newQs
    })
  }, [])

  const handleAddQuestion = (qType: ContentItemType) => {
    setNewQuestions((prev) => {
      const newQ: any = {
        id: Date.now(),
        type: qType,
        index: prev.length,
      }

      // Seed LearningCheckbox with explicit fields for creation payload
      if (qType === ContentItemType.LearningCheckbox) {
        newQ.label = ''
      }

      const updatedQuestions = [...prev, newQ]
      return updateIndices(updatedQuestions)
    })
  }

  const handleDeleteQuestion = (idOrTempId: string | number) => {
    setNewQuestions((prev) => {
      const filteredQuestions = prev.filter((q) => q.id !== idOrTempId && q.tempId !== idOrTempId)
      return updateIndices(filteredQuestions)
    })

    setQuestionErrors((prev) => {
      const newErrors = { ...prev }
      delete newErrors[idOrTempId as number]
      return newErrors
    })
  }

  // Survey/Learning answer handlers
  const handleSurveyMCQSelect = (questionId: string, selectedIndex: number) => {
    setSurveyMCQSelections((prev) => ({ ...prev, [questionId]: selectedIndex }))
  }

  const handleSurveyRatingSelect = (questionId: string, rating: number) => {
    setSurveyRatingSelections((prev) => ({ ...prev, [questionId]: rating }))
  }

  const handleSurveyTextAnswer = (questionId: string, answer: string) => {
    setSurveyTextAnswer((prev) => ({ ...prev, [questionId]: answer }))
  }

  const handlePublish = async () => {
    if (!contentId) return
    try {
      if (isDraft) {
        await patchPublishContentMutation.mutateAsync(contentId.toString())
      } else {
        await patchContent(contentId.toString(), {
          is_draft: true,
        })
        handleToast(toast, true, t('content.toasts.unpublishedTitle'), t('content.toasts.unpublishedMessage'))
        queryClient.invalidateQueries({ queryKey: ['content'] })
      }
    } catch (error) {
      handleToast(toast, false, t('content.toasts.publishFailedTitle'), t('content.toasts.publishFailedMessage'))
    }
  }

  const handleCompleteContent = async () => {
    try {
      setIsSaving(true)
      // Build survey/learning responses (only when not editing and content is survey/learning)
      if (!isEditing && (contentType === ContentType.Survey || contentType === ContentType.Learning)) {
        const surveyAnswers = newQuestions
          .filter((q) =>
            (q.type === ContentItemType.SurveyMCQ && q.id.toString() in surveyMCQSelections) ||
            (q.type === ContentItemType.SurveyRating && q.id.toString() in surveyRatingSelections) ||
            (q.type === ContentItemType.SurveyText && q.id.toString() in surveyTextAnswer) ||
            // Only include learning checkbox when checked
            (q.type === ContentItemType.LearningCheckbox && surveyTextAnswer[q.id.toString()] === 'true'),
          )
          .map((q) => {
            const qIdString = q.id.toString()
            let answer: any = ''
            if (q.type === ContentItemType.SurveyMCQ) {
              const idx = surveyMCQSelections[qIdString]
              answer = idx != null && Array.isArray(q.answers) ? q.answers[idx] ?? '' : ''
            } else if (q.type === ContentItemType.SurveyRating) {
              answer = surveyRatingSelections[qIdString] ?? null
            } else if (q.type === ContentItemType.SurveyText) {
              answer = surveyTextAnswer[qIdString] ?? null
            } else if (q.type === ContentItemType.LearningCheckbox) {
              answer = true
            }
            return {
              type: q.type,
              question: qIdString,
              answer,
            }
          })

        if (surveyAnswers.length > 0) {
          try {
            await addContentResponse(contentId, { answers: surveyAnswers })
          } catch (e) {
            console.error('Failed to submit survey/learning responses before completing:', e)
          }
        }
      }

      await completeContent(contentId)
      setIsCompleted(true)
      handleToast(toast, true, t('content.toasts.completedTitle'), '')
      await queryClient.invalidateQueries({ queryKey: ['contentItemsList', String(contentId)] })
      
      // Call onComplete callback if provided
      if (onComplete) {
        onComplete()
      }
    } catch (error) {
      console.error('Error completing content:', error)
      handleToast(
        toast,
        false,
        t('content.toasts.completeFailedTitle'),
        error instanceof Error ? error.message : t('common.labels.unknownError'),
      )
    } finally {
      setIsSaving(false)
    }
  }

  const toast = useToast()

  const [isSaving, setIsSaving] = useState<boolean>(false)

  const handleUpdateContent = async (draft: boolean) => {
    try {
      if (draft) {
        const invalidQuestions = newQuestions.filter(
          (q) =>
            (q.type === ContentItemType.TrainingMCQ || q.type === ContentItemType.TrainingCheckbox) &&
            (!q.answers ||
              !q.answers.some((a: any) => a.correct) ||
              !q.question ||
              !q.correct_feedback ||
              !q.incorrect_feedback),
        )

        if (invalidQuestions.length > 0) {
          const invalidCards = invalidQuestions
            .map((q) => {
              const errors = []
              if (!q.answers) {
                errors.push(t('content.validation.noOptionsAdded'))
              } else if (!q.answers.some((a: any) => a.correct)) {
                errors.push(t('content.validation.noCorrectAnswersSelected'))
              }
              if (!q.question) {
                errors.push(t('content.validation.missingQuestionText'))
              }

              return t('content.validation.cardErrorLine', {
                cardNumber: q.index + 1,
                type: q.type,
                errors: errors.join(', '),
              })
            })
            .join('\n')

          handleToast(
            toast,
            false,
            t('content.toasts.cannotSaveDraftTitle'),
            t('content.validation.someCardsHaveErrors', { errors: invalidCards }),
            8000,
          )
          return
        }
      }

      setIsSaving(true)

      const originalQuestions = contentItemsQuery.data?.data || []

      const originalQuestionsMap = new Map(oldQuestions.map((q) => [q.id, q]))
      const newQuestionsMap = new Map(newQuestions.map((q) => [q.id, q]))

      const toDelete = originalQuestions.filter((q: any) => !newQuestionsMap.has(q.id))
      const toCreate = newQuestions.filter((q) => !originalQuestionsMap.has(q.id))
      const toUpdate = newQuestions.filter((q) => {
        const original = originalQuestionsMap.get(q.id)
        return original && JSON.stringify(original) !== JSON.stringify(q)
      })

      try {
        for (const q of toDelete) {
          await deleteContentItemMutation.mutateAsync({ id: contentId, itemId: q.id })
        }

        for (const q of toUpdate) {
          await patchContentItemMutation.mutateAsync({
            id: contentId,
            itemId: q.id,
            contentItem: buildPayload(q, q.id),
          })
        }

        for (const q of toCreate) {
          await createContentItemMutation.mutateAsync({
            id: contentId,
            contentItem: buildPayload(q),
          })
        }

        await queryClient.invalidateQueries({ queryKey: ['contentItemsList', String(contentId)] })

        if (draft) {
          handleToast(toast, true, t('content.toasts.updatedTitle'), '', 3000)
        }
      } catch (error) {
        console.error('Error during content update operations:', error)
        throw error
      }
    } catch (error: any) {
      console.error('Failed to save content & questions:', error)
      handleToast(
        toast,
        false,
        t('content.toasts.saveFailedTitle'),
        error?.message || t('common.labels.unexpectedError'),
      )
    } finally {
      setIsSaving(false)
    }
  }
  const buildPayload = (question: any, id?: string) => {
    const payload: any = {
      type: question.type,
      index: question.index,
    }

    if (id) payload.id = id

    switch (question.type) {
      case ContentItemType.Media:
        if (question.media instanceof File) {
          payload.media = question.media
        }
        break

      case ContentItemType.Section:
        payload.title = question.title
        break

      case ContentItemType.Paragraph:
        payload.title = question.title
        payload.paragraph = question.paragraph
        break

      case ContentItemType.Short:
        payload.title = question.title
        payload.short = question.short
        break

      case ContentItemType.LearningCheckbox:
        // Backend expects only 'label'; map from the editable title field
        payload.label = question.label || question.text || question.title || ''
        break

      default:
        payload[question.type] = question[question.type]
        break
    }

    return payload
  }

  const handleUpdate = (index: number, updatedFields: any) => {
    setNewQuestions((prev) => {
      const newQs = [...prev].map((q) => ({ ...q }))
      const question = { ...newQs[index] }

      if (updatedFields.type && updatedFields.type !== question.type) {
        const updatedQuestion: any = {
          id: question.id,
          index: question.index,
          type: updatedFields.type,
        }

  switch (updatedFields.type) {
          case 'short':
            updatedQuestion.short = ''
            break
          case 'paragraph':
            updatedQuestion.paragraph = ''
            break
          case 'media':
            updatedQuestion.media = ''
            break
          case 'url':
            updatedQuestion.url = ''
            break
          case ContentItemType.LearningCheckbox:
            updatedQuestion.content = ''
            updatedQuestion.label = ''
            break
        }

        newQs[index] = updatedQuestion
      } else {
        const updatedQuestion = { ...question }

        if (question.type === ContentItemType.Section) {
          updatedQuestion.title = updatedFields.text || question.text || ''
        } else if (question.type === ContentItemType.Paragraph) {
          updatedQuestion.title = updatedFields.text || question.text || ''
          updatedQuestion.paragraph = updatedFields.paragraph || question.paragraph || ''
        } else if (question.type === ContentItemType.Short) {
          updatedQuestion.title = updatedFields.text || question.text || ''
          updatedQuestion.short = updatedFields.short || question.short || ''
        } else if (question.type === ContentItemType.LearningCheckbox) {
          // Keep title and label in sync for LearningCheckbox
          const nextText = updatedFields.text || question.title || ''
          updatedQuestion.title = nextText
          updatedQuestion.label = nextText
        } else {
          updatedQuestion[question.type] = updatedFields.text || ''
        }

        newQs[index] = updatedQuestion
      }

      if (updatedFields?.text !== undefined || updatedFields?.file !== undefined) {
        if ((typeof updatedFields.text === 'string' && updatedFields.text.trim() !== '') || updatedFields.file) {
          setQuestionErrors((prevErrors) => {
            const newErrors = { ...prevErrors }
            delete newErrors[newQs[index].id]
            return newErrors
          })
        }
      }

      return newQs
    })
  }

  const toggleEditing = () => {
    if (isEditing) {
      const invalidQuestions = newQuestions.filter((q) => {
        if (q.type === ContentItemType.TrainingMCQ || q.type === ContentItemType.TrainingCheckbox) {
          return (
            !q.answers ||
            !q.answers.some((a: any) => a.correct) ||
            !q.question ||
            !q.correct_feedback ||
            !q.incorrect_feedback
          )
        } else if (q.type === 'short' || q.type === 'paragraph') {
          return !q[q.type] || q[q.type].trim() === ''
        } else if (q.type === 'media') {
          return !q.media
        } else if (q.type === 'url') {
          return !q.url || q.url.trim() === ''
        } else if (q.type === 'section') {
          return !q.title || q.title.trim() === ''
        } else if (q.type === 'survey_multiple_choice' || q.type === 'survey_rating') {
          return !q.question || q.question.trim() === '' || !q.answers || q.answers.length < 2
        }
        return false
      })

      if (invalidQuestions.length > 0) {
        const invalidCards = invalidQuestions
          .map((q) => {
            const errors = []
            if (q.type === ContentItemType.TrainingMCQ || q.type === ContentItemType.TrainingCheckbox) {
              if (!q.answers) {
                errors.push(t('content.validation.noOptionsAdded'))
              } else if (!q.answers.some((a: any) => a.correct)) {
                errors.push(t('content.validation.noCorrectAnswersSelected'))
              }
              if (!q.question) {
                errors.push(t('content.validation.missingQuestionText'))
              }
              if (!q.correct_feedback || !q.incorrect_feedback) {
                errors.push(t('content.validation.missingFeedback'))
              }
            } else if (q.type === 'short' || q.type === 'paragraph') {
              errors.push(t('content.validation.missingText'))
            } else if (q.type === 'media') {
              errors.push(t('content.validation.missingMedia'))
            } else if (q.type === 'url') {
              errors.push(t('content.validation.missingUrl'))
            } else if (q.type === 'section') {
              errors.push(t('content.validation.missingSectionTitle'))
            } else if (q.type === 'survey_multiple_choice' || q.type === 'survey_rating') {
              if (!q.question) {
                errors.push(t('content.validation.missingQuestionText'))
              }
              if (!q.answers || q.answers.length < 2) {
                errors.push(t('content.validation.needAtLeastTwoAnswerOptions'))
              }
            }

            return t('content.validation.cardErrorLine', {
              cardNumber: q.index + 1,
              type: q.type,
              errors: errors.join(', '),
            })
          })
          .join('\n')

        handleToast(
          toast,
          false,
          t('content.toasts.cannotSaveContentTitle'),
          t('content.validation.someCardsHaveErrors', { errors: invalidCards }),
          8000,
        )
        return
      }

      handleUpdateContent(true)
    }
    setIsEditing(!isEditing)
  }

  // const unsavedChanges = JSON.stringify(newQuestions) !== JSON.stringify(contentItemsQuery.data?.data)

  return (
    <Flex w="100%" h="100%" overflowY="auto" bg={CONTENT_EXPERIENCE.pageBg}>
      <Box ref={startComponentRef} h="1px" />
      <Flex flex="1" direction="column" borderRadius={CONTENT_EXPERIENCE.panelRadius} alignItems="center" height="auto">
        <Flex w="100%" position="relative">
          <Flex flex="1" mx={6}>
            <VStack
              width="100%"
              align="stretch"
              borderRadius={CONTENT_EXPERIENCE.panelRadius}
              bg={CONTENT_EXPERIENCE.glassPanelBg}
              border="1px solid"
              borderColor={CONTENT_EXPERIENCE.glassBorder}
              boxShadow={CONTENT_EXPERIENCE.glassShadow}
              backdropFilter="blur(14px)"
              p="20px"
            >
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="newQuestions" isDropDisabled={!isEditing}>
                  {(provided: any) => (
                    <VStack spacing="16px" ref={provided.innerRef} {...provided.droppableProps}>
                      {contentType === ContentType.Learning && normalizedLearningUrl && (
                        <Box
                          w="100%"
                          borderWidth="1px"
                          borderColor="solid.100"
                          borderRadius={CONTENT_EXPERIENCE.cardRadius}
                          bg="background.White"
                          boxShadow="none"
                          px="20px"
                          py="18px"
                        >
                          <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} gap="14px" direction={{ base: 'column', md: 'row' }}>
                            <Box>
                              <Text
                                fontSize="12px"
                                fontWeight="700"
                                color="solid.500"
                                textTransform="uppercase"
                                letterSpacing="0.08em"
                              >
                                {showTakingMode ? t('content.actions.startLearning') : t('content.actions.editLearning')}
                              </Text>
                              <Text mt="6px" fontSize="18px" fontWeight="700" color="solid.900">
                                {showTakingMode ? 'Open the learning material for this content' : 'Review the linked learning destination'}
                              </Text>
                              <Text mt="6px" fontSize="14px" color="solid.600">
                                {showTakingMode
                                  ? 'Launch the external training, come back here, then complete the confirmation and feedback steps.'
                                  : 'This is the destination learners will open while taking the content.'}
                              </Text>
                            </Box>
                            <Button
                              as="a"
                              href={normalizedLearningUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="mono"
                              size="sm"
                              alignSelf={{ base: 'stretch', md: 'auto' }}
                            >
                              {t('content.actions.takeMeToTraining')}
                            </Button>
                          </Flex>
                        </Box>
                      )}
            {   /*       <Flex justifyContent="flex-end" alignItems="center" w="97%" mb="16px" mt="8px" ml="32px">
                        {!isModal && (
                          <>
                            <Button variant="mono" onClick={toggleEditing} isLoading={isSaving} size="sm">
                              {isEditing ? t('content.actions.finishEditingContent') : t('content.actions.editContent')}
                            </Button>

                            {isEditing && (
                              <Button variant="mono" onClick={() => handlePublish()} size="sm" ml="8px">
                                {isDraft ? t('content.actions.publishContent') : t('content.actions.unpublishContent')}
                              </Button>
                            )}
                          </>
                        )}
                      </Flex>
                      
*/}
                      {Array.isArray(newQuestions) && newQuestions.length > 0 && (
                        <Flex justifyContent="flex-end" alignItems="center" w="97%" mb="16px" mt="8px" ml="32px" gap="8px" flexWrap="wrap">
                          {canEditContent && !isModal && (
                            <Button variant="mono" onClick={onEditNavigate} size="sm">
                              {t('content.actions.editContent')}
                            </Button>
                          )}

                          {isModal && isAdmin && !isUserMode && (
                            <>
                              <Button variant="mono" onClick={toggleEditing} isLoading={isSaving} size="sm">
                                {isEditing ? t('content.actions.finishEditingContent') : t('content.actions.editContent')}
                              </Button>

                              {isEditing && (
                                <Button variant="mono" onClick={() => handlePublish()} size="sm">
                                  {isDraft ? t('content.actions.publishContent') : t('content.actions.unpublishContent')}
                                </Button>
                              )}
                            </>
                          )}
                        </Flex>
                      )}

                      {Array.isArray(newQuestions) && newQuestions.length === 0 ? (
  !isModal && canEditContent ? (
    <Box w="100%" h = "150px">
      <Button
        onClick={onEditNavigate}
        variant="unstyled"
        w="auto"
      >
        <Flex
          borderWidth="1px"
          borderRadius={CONTENT_EXPERIENCE.cardRadius}
          minH="100px"
          borderColor="solid.100"
          bg="background.White"
          overflow="hidden"
          position="relative"
          align="center"
          boxShadow="none"
          px="20px"
          py="20px"
        >
          <Flex direction="column" align="flex-start" justify="center">
            <Text
              fontSize="12px"
              fontWeight="600"
              color="solid.500"
              mb="8px"
              textTransform="uppercase"
              letterSpacing="0.5px"
            >
              {t('content.actions.editContent')}
            </Text>
            <Text
              fontSize="16px"
              fontWeight="600"
              color="solid.900"
              textAlign="left"
            >
            </Text>
          </Flex>
        </Flex>
      </Button>
    </Box>
  ) : (
    <Text mt="60px" color="solid.200" textStyle="l-md">
      {t('content.empty.noContentAvailable')}
    </Text>
  )
) : (
                        Array.isArray(newQuestions) &&
                        newQuestions
                          .filter((q) => {
                            if (!isEditing) {
                              if (q.type === 'short' || q.type === 'paragraph') {
                                return q[q.type] && q[q.type].trim() !== ''
                              } else if (q.type === 'media') {
                                return q.media
                              } else if (q.type === 'url') {
                                return q.url && q.url.trim() !== ''
                              } else if (q.type === 'section') {
                                return q.title && q.title.trim() !== ''
                              }
                            }
                            return true
                          })
                          .map((q, idx) => {
                            const displayIndex = newQuestions
                              .slice(0, idx)
                              .filter((question) =>
                                [
                                  'short',
                                  'paragraph',
                                  'survey_multiple_choice',
                                  'survey_rating',
                                  'training_multiple_choice',
                                  'training_checkbox',
                                  'checkbox'
                                ].includes(question.type),
                              ).length

                            return (
                              <Draggable key={q.id} draggableId={String(q.id)} index={idx} isDragDisabled={!isEditing}>
                                {(provided: any) => (
                                  <Box
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...(isEditing ? provided.dragHandleProps : {})}
                                    style={{ width: '100%', ...provided.draggableProps.style }}
                                    borderRadius={q.type === 'section' ? '0' : '12px'}
                                    overflow={q.type === 'section' ? 'visible' : 'hidden'}
                                    bg={q.type === 'section' ? 'transparent' : 'background.white'}
                                    transition="box-shadow 0.15s ease"
                                    boxShadow="sm"
                                  >
                                    <Flex alignItems="flex-start">
                                      {isEditing && (
                                        <Icon boxSize={'20px'} mt="8px" color="solid.400" mr="8px">
                                          <DragGridIcon />
                                        </Icon>
                                      )}
                                      <MasterCard
                                        isEditing={isEditing}
                                        isReadOnly={!isEditing}
                                        quizMode={!isEditing}
                                        media={q.media}
                                        id={q.id}
                                        index={['short', 'paragraph', 'checkbox', 'survey_multiple_choice', 'survey_rating'].includes(q.type) ? displayIndex : 0}
                                        type={q.type}
                                        text={q.url || q.question || q.title || q.label || ''}
                                        label={q.label || q.short || ''}
                                        paragraph={q.paragraph || ''}
                                        short={q.short || ''}
                                        mcqAnswers={q.answers || []}
                                        // Survey/Learning interactions
                                        onMCQSelect={(questionId, selectedIndex) => handleSurveyMCQSelect(questionId, selectedIndex)}
                                        mcqSelectedIndex={surveyMCQSelections[String(q.id)] ?? null}
                                        onRatingSelect={(questionId, rating) => handleSurveyRatingSelect(questionId, rating)}
                                        ratingValue={surveyRatingSelections[String(q.id)] ?? null}
                                        onAnswer={(questionId, answer) => handleSurveyTextAnswer(questionId, answer)}
                                        contentType={contentType ? contentType : ContentType.Skills}
                                        handleDelete={() => handleDeleteQuestion(q.id)}
                                        handleAddQuestion={handleAddQuestion}
                                        handleAddSection={() => {}}
                                        moveCard={moveQuestion}
                                        correct_feedback={q.correct_feedback}
                                        incorrect_feedback={q.incorrect_feedback}
                                        handleUpdate={(updatedFields) => handleUpdate(idx, updatedFields)}
                                      />
                                    </Flex>
                                    {questionErrors[q.id] && (
                                      <Box textStyle="b-xs" color="error.100" w="100%" mt={4} ml={8}>
                                        <Text>
                                          <Icon w="16px" h="16px">
                                            <WarningIcon />
                                          </Icon>
                                          {questionErrors[q.id]}
                                        </Text>
                                      </Box>
                                    )}
                                  </Box>
                                )}
                              </Draggable>
                            )
                          })
                      )}
                      {provided.placeholder}
                    </VStack>
                  )}
                </Droppable>
              </DragDropContext>
          
              {isModal && !isCompleted && newQuestions.length > 0 && (
                <Flex
                  position="sticky"
                  w="100%"
                  bg={CONTENT_EXPERIENCE.glassPanelBg}
                  borderRadius={CONTENT_EXPERIENCE.cardRadius}
                  border="1px solid"
                  borderColor={CONTENT_EXPERIENCE.glassBorder}
                  boxShadow={CONTENT_EXPERIENCE.softShadow}
                  zIndex={10}
                  justify="flex-end"
                  py="12px"
                  px="12px"
                  mt="20px"
                >
                  <Button 
                    variant="mono" 
                    size="sm" 
                    onClick={handleCompleteContent}
                    isLoading={isSaving}
                  >
                    {t('common.actions.complete')}
                  </Button>
                </Flex>
              )}
            </VStack>
          </Flex>
          {isEditing && (
            <Box position="relative" width="64px" ml="32px">
              <Box
                ref={sidebarRef}
                position={sidebarPosition === 'start' || stopSidebar ? 'static' : 'fixed'}
                top={sidebarPosition === 'start' ? 'auto' : '175px'}
                bottom={'auto'}
                width="64px"
                transition="all 0.2s ease-in-out"
              >
                <SideBar handleAddQuestion={handleAddQuestion} bigger={true} contentType={contentType} />
              </Box>
            </Box>
          )}
        </Flex>
        <Box ref={endComponentRef} h="1px" />
      </Flex>
    </Flex>
  )
}

export default AboutContentQuestions
